'use client';

import { useCallback, useMemo } from 'react';
import {
  ArrowDown01Icon,
  ArrowUp01Icon,
  AiMagicSolidIcon,
} from '@terra/ui-icons';
import { HStack, Box } from '@terra/ui-styled-system';
import { IconButton, LabelDefault, MonoSmall } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import type { FindingEditableField } from '../../store/findingEditStore';
import { getReviewPillState } from '../../utils/findingPendingEditsUtils';
import { FindingCopilotSuggestedActions } from './FindingCopilotNote';

type FindingPendingEditsBannerProps = {
  sessionFields: FindingEditableField[];
  pendingCount: number;
  focusedField?: FindingEditableField;
  onFocusField: (field: FindingEditableField) => void;
  focusedActions?: {
    onAccept: () => void;
    onDiscard: () => void;
  };
};

export function FindingPendingEditsBanner({
  sessionFields,
  pendingCount,
  focusedField: focusedFieldProp,
  onFocusField,
  focusedActions,
}: FindingPendingEditsBannerProps) {
  const t = useTranslations(
    'designSystem.findings.editDrawer.suggestionReview',
  );
  const total = sessionFields.length;
  const focusedIndex = useMemo(() => {
    if (!focusedFieldProp) {
      return 0;
    }

    const index = sessionFields.indexOf(focusedFieldProp);

    return index >= 0 ? index : 0;
  }, [focusedFieldProp, sessionFields]);
  const pillState = getReviewPillState({
    sessionFields,
    pendingCount,
    focusedField: focusedFieldProp,
  });

  const navigateToIndex = useCallback(
    (nextIndex: number) => {
      const field = sessionFields[nextIndex];

      if (!field) {
        return;
      }

      onFocusField(field);
    },
    [onFocusField, sessionFields],
  );

  const handlePrevious = useCallback(() => {
    navigateToIndex(Math.max(0, focusedIndex - 1));
  }, [focusedIndex, navigateToIndex]);

  const handleNext = useCallback(() => {
    navigateToIndex(Math.min(total - 1, focusedIndex + 1));
  }, [focusedIndex, navigateToIndex, total]);

  if (pillState.kind === 'hidden') {
    return null;
  }

  const canGoPrevious = focusedIndex > 0;
  const canGoNext = focusedIndex < total - 1;
  const fieldLabel = t.has(`fieldLabels.${pillState.field}`)
    ? t(`fieldLabels.${pillState.field}`)
    : pillState.field;

  return (
    <HStack className="review-pill" alignItems="center" gap={2} w="max-content">
      <Box flexShrink={0} display="flex" color="fg.accent.default" w="3" h="3">
        <AiMagicSolidIcon size={12} />
      </Box>
      <LabelDefault as="span" color="fg.neutral.default" whiteSpace="nowrap">
        {t('reviewingField', {
          field: fieldLabel,
        })}
      </LabelDefault>
      <MonoSmall color="fg.neutral.subtle" whiteSpace="nowrap">
        {t('changeCounter', {
          current: pillState.current,
          total: pillState.total,
        })}
      </MonoSmall>
      <Box
        flexShrink={0}
        w="1px"
        h="4"
        bg="border.neutral.subtle"
        aria-hidden
      />
      <HStack gap={0} alignItems="center" flexShrink={0}>
        <IconButton
          type="button"
          size="xs"
          variant="outline"
          aria-label={t('previousChange')}
          onClick={handlePrevious}
          disabled={!canGoPrevious}
          w="5"
          h="5"
          minW="5"
          px={1}
          borderTopRightRadius={0}
          borderBottomRightRadius={0}
          mr="-1px"
        >
          <ArrowUp01Icon size={12} />
        </IconButton>
        <IconButton
          type="button"
          size="xs"
          variant="outline"
          aria-label={t('nextChange')}
          onClick={handleNext}
          disabled={!canGoNext}
          w="5"
          h="5"
          minW="5"
          px={1}
          borderTopLeftRadius={0}
          borderBottomLeftRadius={0}
        >
          <ArrowDown01Icon size={12} />
        </IconButton>
      </HStack>
      {focusedActions ? (
        <FindingCopilotSuggestedActions
          onAccept={focusedActions.onAccept}
          onDiscard={focusedActions.onDiscard}
          acceptVariant="solid"
        />
      ) : null}
    </HStack>
  );
}
